import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authResetPassword } from "@/lib/api";
import { useSpark } from "@/lib/store";
import { BrandHeader } from "@/components/BrandHeader";
import { PublicFooter } from "@/components/PublicFooter";

const errText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map(e => e?.msg).filter(Boolean).join(" ") : "Something went wrong. Please try again.");

export default function ResetPassword() {
  const nav = useNavigate();
  const { setSession, switchUser } = useSpark();
  const token = useRef(new URLSearchParams(window.location.search).get("token") || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Those passwords don't match."); return; }
    setBusy(true); setError("");
    try {
      const { user } = await authResetPassword(token.current, password);
      setSession(user); switchUser(user.user_id);
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(errText(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <BrandHeader links={[]} />
      <main className="flex-1" style={{ background: "hsl(var(--spark-cool))" }}>
        <div className="spark-narrow py-20 sm:py-28">
          <div data-testid="reset-card" className="spark-card mx-auto max-w-xl px-7 py-10 sm:px-12 sm:py-14">
            <img src="/brand/monogram_black.png" alt="" aria-hidden="true" className="h-14 w-14" />
            <p className="spark-eyebrow-copper mt-8">Reset your password</p>
            <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">Choose a new password.</h1>
            {!token.current ? (
              <p data-testid="reset-missing" className="mt-6 text-base" style={{ color: "hsl(var(--spark-ink-soft))" }}>This link is incomplete. <Link to="/signin" className="underline">Ask for a new one</Link>.</p>
            ) : (
              <form onSubmit={submit} className="mt-8" data-testid="reset-form">
                <label className="spark-eyebrow block mb-2" htmlFor="reset-password">New password</label>
                <input id="reset-password" data-testid="reset-password-input" type="password" required minLength={8} value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters"
                       className="w-full rounded-xl border bg-white px-4 py-3.5 text-base focus:outline-none focus:ring-2" style={{ borderColor: "hsl(var(--input))", "--tw-ring-color": "rgba(44,80,160,0.35)" }} />
                <label className="spark-eyebrow block mt-5 mb-2" htmlFor="reset-confirm">Confirm password</label>
                <input id="reset-confirm" data-testid="reset-confirm-input" type="password" required minLength={8} value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Once more"
                       className="w-full rounded-xl border bg-white px-4 py-3.5 text-base focus:outline-none focus:ring-2" style={{ borderColor: "hsl(var(--input))", "--tw-ring-color": "rgba(44,80,160,0.35)" }} />
                <button data-testid="reset-submit-btn" disabled={busy || password.length < 8} className="spark-btn-primary mt-6 w-full sm:w-auto disabled:opacity-60">
                  {busy ? "Saving…" : "Save and sign in"}
                </button>
                {error && <p data-testid="reset-error" className="mt-3 text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</p>}
              </form>
            )}
            <p className="mt-10 pt-5 text-sm spark-rule" style={{ color: "hsl(var(--spark-ink-soft))" }}>
              Remembered it? <Link to="/signin" data-testid="reset-signin-link" className="font-medium underline underline-offset-2 text-[#2C50A0]">Sign in</Link>
            </p>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
